/**
 * @file 首页
 * @author gaoxunchen
 */

import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { runInAction } from 'mobx';
import { Loading } from '@tencent/ptp-fe-common';
import dayjs from 'dayjs';
import classnames from 'classnames';

import { hasPurview, hasFeatureFlag } from '@/services';
import globalStore from '@/stores';
import { getRainbowCommonConfig } from '@/api/rainbow';
import { isEnableAIAssistant } from '@/ai-assistant';

import store from './store';
import { getTotalCostData } from './api';
import CardCostSummary from './components/card-cost-summary';
import CardCostDetail from './components/card-cost-detail';
import CardTask from './components/card-task';
import CardAccountEffect from './components/card-accout-effect';
import HomeSaleTodo from '@/business-components/sale/HomeSaleTodo/index';
import HomeSaleBoard from '@/business-components/sale/HomeSaleBoard/index';
import ChanceBoard from '@/pages/agency/account/chance-customer/components/chanceBoard/index';
import CardAssets from './components/card-assets';
import Announcement from './components/announcement';
import Advertising from './components/advertising';
import DialogWelcome from './components/dialog-welcome';
// import AuthUserTypeDialog from './components/user-auth';
import ThrowControl from './components/throw-control';
import AICardEntry from './components/ai-card-entry';

import './index.scoped.less';

enum CostSummaryType {
  // 今日
  today = 'today',
  // 昨日
  yesterday = 'yesterday',
}

const Container: React.FC = () => {
  const { account } = globalStore;
  const { operator_name, agency_uid } = account || {};
  const [config, setConfig] = useState<Record<string, any>>({});

  useEffect(() => {
    getConfig();
  }, []);

  useEffect(() => {
    getData(CostSummaryType.today);
    getData(CostSummaryType.yesterday);
  }, []);

  const getData = async (type: CostSummaryType) => {
    const parmas = {
      type,
    };
    try {
      const res = await getTotalCostData({
        key: `getTotalCost-${type}`,
        data: parmas,
      });
      const data = _.get(res, `data.${type}`, {});
      runInAction(() => {
        store[`${type}CostData`] = data;
      });
    } catch (error) { }
  };

  const schemaMap = {
    'card-cost-summary': {
      component: CardCostSummary,
      display: () => !isEnableAIAssistant(),
    },
    'card-cost-detail': {
      component: CardCostDetail,
    },
    'card-task': {
      component: CardTask,
    },
    'chance-board-data': {
      component: ChanceBoard,
    },
    'card-account-effect': {
      component: CardAccountEffect,
    },
    'card-announcement': {
      component: Announcement,
      display: () => !isEnableAIAssistant(),
    },
    'card-advertising': {
      component: Advertising,
    },
    'home-sale-data': {
      component: HomeSaleBoard,
    },
    'home-sale-todo': {
      component: HomeSaleTodo,
    },
    'card-assets': {
      component: CardAssets,
    },
    'home-ai-card': {
      component: AICardEntry,
      display: () => isEnableAIAssistant(),
    },
  };

  const schema = _.get(config, 'home', []).map((v: any) => ({
    ...v,
    ...(schemaMap[v?.key] || {}),
  }));

  const getConfig = async () => {
    try {
      const config = await getRainbowCommonConfig('home_config');
      setConfig(config);
      // 首屏置灰
      if (config?.bodyGray) {
        (document.querySelector('#root'))?.classList.add('body-gray');
      }
    } catch (error) {
    }
  };

  if (_.isEmpty(schema)) {
    return <Loading />;
  }

  return (
    <div className="home">
      <ThrowControl style={{ marginTop: '12px' }} needShowConfirmAuthMessage={false} />
      <div className={classnames('home-header', { mini: isEnableAIAssistant() })}>
        {!isEnableAIAssistant() && <div className="title">{`${operator_name}，${dayjs().hour() < 13 ? '上午' : '下午'}好`}</div>}
        {/* <AuthUserTypeDialog /> */}
      </div>
      <div className="home-body">
        {
          schema.map((v: any) => {
            const { key, title, component: Component, componentProps = {}, display, perm, permType = 'allOf', enableKey, disableKey, role, disableRole } = v;
            if (display && !display()) {
              return null;
            }
            if (perm && !hasPurview(perm, permType)) {
              return null;
            }
            if (enableKey && !hasFeatureFlag(enableKey)) {
              return null;
            }
            if (disableKey && hasFeatureFlag(disableKey)) {
              return null;
            }
            if (!Component) {
              return null;
            }
            return (
              <Component
                key={key}
                schema={_.get(config, key, {})}
                {...componentProps}
                {...{
                  title,
                  perm,
                  account,
                }}
              />
            );
          })
        }
      </div>
      <DialogWelcome
        schema={_.get(config, 'dialog-welcome', {})}
      />
    </div>
  );
};

export default Container;
